import type { BudgetReviewsResponse } from "@performyard/service-contracts";
import { toFixed } from "@performyard/utils-isomorphic";

import type { DecisionRow, DecisionRowApprovalSnapshot } from "../../types";
import type { GroupBudgetFigures, IncreaseValues } from "../MyDecisions/types";

export const pluralize = (count: number, singular: string, plural: string): string => (count === 1 ? singular : plural);

type ManagerGroup = BudgetReviewsResponse["individualBudgets"][string];

export type ManagerBudgetGroupWithCycleAllotment = ManagerGroup & {
	allotmentIncrease: number;
	allotmentIncreasePercent: number | null;
};

const attachCycleAllotment = (
	group: ManagerGroup,
	allotmentIncrease: number,
	allotmentIncreasePercent: number | null
): ManagerBudgetGroupWithCycleAllotment => ({
	...group,
	allotmentIncrease,
	allotmentIncreasePercent,
});

export const budgetGroupKey = (group: ManagerGroup): string =>
	group.overrideManagerId ? `${group.managerId}:${group.overrideManagerId}` : group.managerId;

/** Keeps the first group per line manager, or per manager + override pair when an override exists. */
export const dedupeBudgetGroupsByManagerId = (groups: ManagerGroup[]): ManagerGroup[] => {
	const byKey = new Map<string, ManagerGroup>();
	for (const group of groups) {
		const key = budgetGroupKey(group);
		if (!byKey.has(key)) {
			byKey.set(key, group);
		}
	}
	return [...byKey.values()];
};

const hasResults = (g: ManagerGroup): boolean => g.results.length > 0;

const isApprovedManagerGroup = (group: ManagerGroup): boolean => hasResults(group) && group?.status === "approved";

const isInReviewManagerGroup = (group: ManagerGroup): boolean => hasResults(group) && group?.status === "in_review";

const isOverBudget = (group: ManagerGroup, cycleAllotmentIncreasePercent: number): boolean => {
	if (!hasResults(group)) return false;
	const totalProposedIncrease = group.results.reduce((sum, r) => sum + (r.proposedIncreaseAmount ?? 0), 0);
	const totalCurrentSalary = group.results.reduce((sum, r) => sum + (r.currentSalary ?? 0), 0);
	return toFixed(totalProposedIncrease, 2) > toFixed(totalCurrentSalary * (cycleAllotmentIncreasePercent / 100), 2);
};
const calculateMedian = (values: number[]): number | undefined => {
	if (values.length === 0) return undefined;
	const sorted = [...values].sort((a, b) => a - b);
	const mid = Math.floor(sorted.length / 2);
	return sorted.length % 2 !== 0 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
};

export const parseBudgetReviewsData = (cycleBudgetData: BudgetReviewsResponse | undefined) => {
	const cycleData = cycleBudgetData?.compensationCycleData;
	const individualBudgets = cycleBudgetData?.individualBudgets ?? {};
	const cycleAllotmentIncrease = cycleData?.allotmentIncrease ?? 0;
	const cycleAllotmentIncreasePercent = cycleData?.allotmentIncreasePercent ?? null;
	const managerGroups = dedupeBudgetGroupsByManagerId(Object.values(individualBudgets)).map((group) =>
		attachCycleAllotment(group, cycleAllotmentIncrease, cycleAllotmentIncreasePercent)
	);
	const approvedBudgets = managerGroups.filter(isApprovedManagerGroup);
	const submittedBudgets = managerGroups.filter(isInReviewManagerGroup);
	const notSubmittedBudgets = managerGroups.filter(
		(g) => hasResults(g) && !isApprovedManagerGroup(g) && !isInReviewManagerGroup(g)
	);
	const approvedBudgetCount = approvedBudgets.length;
	const submittedBudgetCount = submittedBudgets.length;
	const notSubmittedBudgetCount = notSubmittedBudgets.length;

	const budgetCount = managerGroups.length;
	const approvedBudgetsPercent = budgetCount === 0 ? "0" : ((approvedBudgetCount / budgetCount) * 100).toFixed(0);
	const notApprovedBudgetsPercent =
		budgetCount === 0 ? "0" : (((notSubmittedBudgetCount + submittedBudgetCount) / budgetCount) * 100).toFixed(0);

	const approvedSpend = approvedBudgets.reduce(
		(total, group) => total + group.results.reduce((sum, row) => sum + (row.proposedIncreaseAmount ?? 0), 0),
		0
	);
	const approvedIncreasesArray = approvedBudgets.flatMap((budget) =>
		budget.results.map((result) => result.proposedIncreaseAmount ?? 0)
	);
	const approvedIncreasePercentsArray = approvedBudgets.flatMap((budget) =>
		budget.results.map((result) => result.proposedIncreasePercent ?? 0).sort((a, b) => a - b)
	);

	const employeesApproved = approvedBudgets.reduce((total, group) => total + group.results.length, 0);
	const submittedSpend = submittedBudgets.reduce(
		(total, group) => total + group.results.reduce((sum, row) => sum + (row.proposedIncreaseAmount ?? 0), 0),
		0
	);
	const employeesSubmitted = submittedBudgets.reduce((total, group) => total + group.results.length, 0);
	const notSubmittedEmployeeCount = notSubmittedBudgets.reduce((total, group) => total + group.results.length, 0);
	const overBudgetBudgets =
		cycleAllotmentIncreasePercent != null
			? managerGroups.filter((g) => isOverBudget(g, cycleAllotmentIncreasePercent))
			: [];

	const approvedPercentSum = approvedIncreasePercentsArray.reduce((sum, p) => sum + p, 0);
	const averageApprovedPercentIncrease = employeesApproved === 0 ? 0 : approvedPercentSum / employeesApproved;

	const medianApprovedIncreasePerEmployee = calculateMedian(approvedIncreasesArray) ?? 0;
	const notApprovedSpend = [...notSubmittedBudgets, ...submittedBudgets].reduce(
		(total, group) => total + group.results.reduce((sum, row) => sum + (row.proposedIncreaseAmount ?? 0), 0),
		0
	);
	return {
		cycleBudget: cycleData?.allotmentIncrease ?? 0,
		approvedSpend,
		approvedIncreasePercentsArray,
		averageApprovedPercentIncrease,
		medianApprovedIncreasePerEmployee,
		submittedSpend,
		employeesApproved,
		employeesSubmitted,
		notSubmittedEmployeeCount,
		employeeCount: cycleData?.budgetSummary?.employeeCount ?? 0,
		percentIncrease: cycleData?.allotmentIncreasePercent ?? null,
		individualBudgets,
		managerGroups,
		budgetCount,
		approvedBudgetCount,
		submittedBudgetCount,
		notSubmittedBudgetCount,
		approvedBudgetsPercent,
		notApprovedBudgetsPercent,
		approvedBudgets,
		submittedBudgets,
		notSubmittedBudgets,
		overBudgetBudgets,
		overBudgetBudgetCount: overBudgetBudgets.length ?? 0,
		notApprovedSpend,
	};
};

type BudgetResultRow = ManagerBudgetGroupWithCycleAllotment["results"][number];

const mapApprovalToSnapshot = (a: NonNullable<BudgetResultRow["approvals"]>[number]): DecisionRowApprovalSnapshot => ({
	approver_id: String(a.approverId),
	approver_name: a.approverName,
	sequence: a.sequence,
	status: a.status,
	decided_at: a.decidedAt != null ? String(a.decidedAt) : null,
	notes: a.notes ?? null,
	amount_at_approval: a.amountAtApproval,
	updated_at: a.updatedAt != null ? String(a.updatedAt) : undefined,
	proposed_increase_amount: a.proposedIncreaseAmount ?? null,
	proposed_increase_percent: a.proposedIncreasePercent ?? null,
	proposed_salary: a.proposedSalary ?? null,
});

export const budgetReviewResultToDecisionRow = (r: BudgetResultRow): DecisionRow => {
	const approvalEntries = (r.approvals ?? []).map(mapApprovalToSnapshot);
	const submittedApproval = [...approvalEntries].reverse().find((e) => e.status === "submitted");
	const fallbackApprover = approvalEntries[0];
	const submitApprover = submittedApproval ?? fallbackApprover;
	const notYetSubmitted = r.status === "pending";
	return {
		_id: String(r.id),
		compensation_cycle_id: String(r.compensationCycle),
		employee_id: String(r.employeeId),
		employee_name: r.employeeName,
		employee_title: "",
		current_salary: r.currentSalary,
		proposed_increase_percent: notYetSubmitted ? null : r.proposedIncreasePercent,
		proposed_increase_amount: notYetSubmitted ? null : r.proposedIncreaseAmount,
		proposed_salary: notYetSubmitted ? null : r.proposedSalary,
		status: r.status,
		notes: r.myDecisionNotes ?? null,
		approver: submitApprover?.approver_id != null ? String(submitApprover.approver_id) : null,
		updatedAt: r.updatedAt != null ? String(r.updatedAt) : null,
		approval_entries: approvalEntries.length > 0 ? approvalEntries : undefined,
	};
};

export const computeAdjustedBudgetFigures = (
	baseRows: DecisionRow[],
	draftOverrides: IncreaseValues,
	groupIncreaseBudget: number
): GroupBudgetFigures => {
	const groupAllocated = baseRows.reduce((total, row) => {
		const draft = draftOverrides[row._id];
		if (draft && typeof draft.amount === "number") {
			return total + draft.amount;
		}
		return total + (row.status !== "pending" ? (row.proposed_increase_amount ?? 0) : 0);
	}, 0);

	const groupPercentAllocated =
		groupIncreaseBudget > 0 ? Math.round((groupAllocated / groupIncreaseBudget) * 10000) / 100 : 0;
	const isGroupOverBudget = groupAllocated - groupIncreaseBudget > 0.01;
	const groupRawRemaining = groupIncreaseBudget - groupAllocated;
	const groupRemaining = Math.abs(groupRawRemaining) < 0.01 ? 0 : toFixed(groupRawRemaining, 2);
	return {
		groupIncreaseBudget,
		groupAllocated,
		groupPercentAllocated,
		groupRemaining,
		isGroupOverBudget: Boolean(isGroupOverBudget),
	};
};

export const mapBudgetGroupResultsToDecisionRows = (
	results: ManagerBudgetGroupWithCycleAllotment["results"]
): DecisionRow[] => results.map((row) => budgetReviewResultToDecisionRow(row));
